<template>
  <div class="behaviour-select-students-fieldset">
    <p
      class="behaviour-select-students-fieldset__legend text-uppercase small text-muted mb-3"
      role="heading"
      aria-level="3"
    >
      {{ $t("behaviour_manage.select_students_fieldset") }}
    </p>

    <fieldset
      v-if="allowCsvUpload"
      class="behaviour-select-students-method border-0 p-0 m-0 mb-3"
    >
      <legend :id="methodLegendId" class="v3-form-label required d-block mb-2">
        {{ $t("behaviour_manage.select_students_using_label") }}
      </legend>
      <div
        class="behaviour-select-students-method__radios d-flex flex-wrap gap-4"
        role="radiogroup"
        :aria-labelledby="methodLegendId"
      >
        <div
          v-for="option in selectionMethodOptions"
          :key="option.value"
          class="behaviour-select-students-method__radio d-flex align-items-center gap-2"
        >
          <input
            :id="`${methodInputIdPrefix}-${option.value}`"
            type="radio"
            :name="methodInputIdPrefix"
            class="behaviour-select-students-method__input"
            :value="option.value"
            :checked="selectionMethod === option.value"
            :data-testid="`${dataTestid}-method-${option.value}`"
            @change="onSelectionMethodChange(option.value)"
          />
          <label
            :for="`${methodInputIdPrefix}-${option.value}`"
            class="behaviour-select-students-method__label mb-0"
          >
            {{ option.label }}
          </label>
        </div>
      </div>
    </fieldset>

    <template v-if="!allowCsvUpload || isMultiselectMethod">
      <Label
        v-if="!allowCsvUpload"
        required
        :label-id="labelId"
        :html-for="inputId"
      >
        {{ $t("behaviour_manage.select_students_label") }}
      </Label>
      <BehaviourStudentLazySelect
        :model-value="modelValue"
        class="w-100"
        multiple
        hide-floating-label
        :labelled-by="allowCsvUpload ? undefined : labelId"
        :input-id="inputId"
        :accessible-label="$t('behaviour_manage.select_students_label')"
        :placeholder="$t('behaviour_manage.select_students_placeholder')"
        :invalid-feedback="invalidFeedback"
        append-dropdown-to-body
        append-dropdown-fixed-position
        :data-testid="dataTestid"
        @update:model-value="$emit('update:modelValue', $event)"
        @changed="$emit('update:modelValue', $event)"
      />
    </template>

    <div
      v-else
      class="behaviour-student-csv-upload"
      data-testid="behaviour-student-csv-upload"
    >
      <FileUpload
        :input-id="csvInputId"
        :accessible-label="$t('behaviour_manage.upload_student_list_csv_label')"
        :uploaded-file="csvFile"
        :invalid-feedback="csvInvalidFeedback"
        @update:model-value="onFileUpload"
        @remove-file="onRemoveFile"
      />
      <p
        :id="csvHelpId"
        class="behaviour-student-csv-upload__help text-start mt-2 mb-0"
      >
        {{ $t("behaviour_manage.csv_help_before_sample") }}
        <a
          class="behaviour-student-csv-upload__link"
          :href="csvSamplePath"
          download
          :data-testid="`${dataTestid}-csv-sample`"
        >
          {{ $t("behaviour_manage.csv_help_sample_link") }}
        </a>
        {{ $t("behaviour_manage.csv_help_between_links") }}
        <router-link
          class="behaviour-student-csv-upload__link"
          :to="fileUploadsPath"
          :data-testid="`${dataTestid}-csv-file-uploads`"
        >
          {{ $t("sidebar_items.file_uploads") }}
        </router-link>
        {{ $t("behaviour_manage.csv_help_after_file_uploads") }}
      </p>
    </div>
  </div>
</template>

<script>
import behaviourPointsConstants from "@/constants/behaviourPointsConstants.js"
import BehaviourStudentLazySelect from "@/v3components/Behaviour/BehaviourStudentLazySelect.vue"
import FileUpload from "@/v3components/shared/Form/FileUpload.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import { computed } from "vue"
import { useI18n } from "vue-i18n"

const {
  STUDENT_SELECTION_METHOD,
  STUDENT_SELECTION_METHOD_VALUES,
  STUDENT_CSV_SAMPLE_PATH
} = behaviourPointsConstants

export default {
  name: "BehaviourSelectStudentsFieldset",
  components: {
    BehaviourStudentLazySelect,
    FileUpload,
    Label
  },
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    selectionMethod: {
      type: String,
      default: STUDENT_SELECTION_METHOD.MULTISELECT,
      validator: (value) => STUDENT_SELECTION_METHOD_VALUES.includes(value)
    },
    csvFile: {
      default: null,
      validator: (value) =>
        value == null || (typeof File !== "undefined" && value instanceof File)
    },
    allowCsvUpload: {
      type: Boolean,
      default: false
    },
    invalidFeedback: {
      type: String,
      default: ""
    },
    csvInvalidFeedback: {
      type: String,
      default: ""
    },
    labelId: {
      type: String,
      default: "behaviour-select-students-lbl"
    },
    inputId: {
      type: String,
      default: "behaviour-select-students-input"
    },
    dataTestid: {
      type: String,
      default: "behaviour-select-students"
    }
  },
  emits: [
    "update:modelValue",
    "update:selectionMethod",
    "update:csvFile",
    "csv-error"
  ],
  setup(props, { emit }) {
    const { t } = useI18n()

    const methodLegendId = computed(() => `${props.dataTestid}-method-legend`)
    const methodInputIdPrefix = computed(() => `${props.dataTestid}-method`)
    const csvInputId = computed(() => `${props.dataTestid}-csv-input`)
    const csvHelpId = computed(() => `${props.dataTestid}-csv-help`)

    const isMultiselectMethod = computed(
      () => props.selectionMethod === STUDENT_SELECTION_METHOD.MULTISELECT
    )

    const selectionMethodOptions = computed(() => [
      {
        value: STUDENT_SELECTION_METHOD.MULTISELECT,
        label: t("behaviour_manage.student_multiselect_option")
      },
      {
        value: STUDENT_SELECTION_METHOD.CSV,
        label: t("behaviour_manage.csv_upload_option")
      }
    ])

    const onSelectionMethodChange = (value) => {
      if (!STUDENT_SELECTION_METHOD_VALUES.includes(value)) return
      emit("update:selectionMethod", value)
      emit("csv-error", "")
      if (value === STUDENT_SELECTION_METHOD.MULTISELECT) {
        emit("update:csvFile", null)
      } else {
        emit("update:modelValue", [])
      }
    }

    const onFileUpload = (event) => {
      const files = event?.target?.files
      if (files?.length) {
        emit("update:csvFile", files[0])
        emit("csv-error", "")
        event.target.value = ""
      }
    }

    const onRemoveFile = () => {
      emit("update:csvFile", null)
      emit("csv-error", "")
    }

    return {
      methodLegendId,
      methodInputIdPrefix,
      csvInputId,
      csvHelpId,
      isMultiselectMethod,
      selectionMethodOptions,
      csvSamplePath: STUDENT_CSV_SAMPLE_PATH,
      fileUploadsPath: {
        path: "/behaviour/point-history",
        query: { tab: "upload-log" }
      },
      onSelectionMethodChange,
      onFileUpload,
      onRemoveFile
    }
  }
}
</script>
